/**
 * Sensor utility library for Trackizy advanced sensor configuration.
 * Supports the sensor definition authoring experience: expression parsing,
 * color intervals, color→hex mapping, and quick-start templates.
 *
 * NOTE: icon-bound helpers (`SENSOR_ICONS`, `resolveSensorIcon`) stay in
 * `src/lib/sensorUtils.js` because they embed lucide-react component
 * references — per packages/CLAUDE.md rule 1 (no framework-specific
 * imports), the shared package must be framework-free.
 */

// ── parseSensorConfig ────────────────────────────────────────────────────────

/** Parse `device.attributes.sensorConfig` into an array.
 *  Returns empty array on null, undefined, malformed JSON, or non-array.
 *  If `raw` is already an array, returns it directly. */
export function parseSensorConfig(raw: unknown): unknown[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== 'string') return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// ── resolveColorInterval ─────────────────────────────────────────────────────

export interface ColorInterval {
  threshold: number | null;
  color: string;
}

/** Given a numeric value and an intervals array, returns the `color` string
 *  of the first interval where `value >= threshold`, after sorting by
 *  threshold descending (null thresholds last — the catch-all). Returns null
 *  when value is null or intervals is empty/missing. */
export function resolveColorInterval(
  value: number | null | undefined,
  intervals: ColorInterval[] | null | undefined,
): string | null {
  if (value == null) return null;
  if (!intervals || intervals.length === 0) return null;

  // Sort: numeric thresholds descending, then null thresholds (catch-all) last
  const sorted = [...intervals].sort((a, b) => {
    if (a.threshold == null && b.threshold == null) return 0;
    if (a.threshold == null) return 1;
    if (b.threshold == null) return -1;
    return b.threshold - a.threshold;
  });

  for (const interval of sorted) {
    if (interval.threshold == null) return interval.color; // catch-all
    if (value >= interval.threshold) return interval.color;
  }

  return null;
}

// ── colorStatusToHex ─────────────────────────────────────────────────────────

/** Map a color status string to its hex color. */
export function colorStatusToHex(colorStatus: string | null | undefined): string {
  switch (colorStatus) {
    case 'green':
      return '#10b981';
    case 'yellow':
      return '#f59e0b';
    case 'red':
      return '#ef4444';
    default:
      return '#64748b';
  }
}

// ── SENSOR_TEMPLATES ─────────────────────────────────────────────────────────

export interface SensorTemplate {
  name: string;
  attribute: string;
  expression: string;
  type: string;
  unit: string;
  icon: string;
  validation: { min?: number; max?: number } | null;
  intervals: ColorInterval[];
}

/** Quick-start templates for common sensor types. Each template provides
 *  name, attribute, expression, type, unit, icon key, optional validation
 *  object, and optional color intervals array. */
export const SENSOR_TEMPLATES: SensorTemplate[] = [
  {
    name: 'Fuel Level',
    attribute: 'fuelPercent',
    expression: 'fuel',
    type: 'Number',
    unit: '%',
    icon: 'Fuel',
    validation: { min: 0, max: 100 },
    intervals: [
      { threshold: 50, color: 'green' },
      { threshold: 20, color: 'yellow' },
      { threshold: null, color: 'red' },
    ],
  },
  {
    name: 'Temperature',
    attribute: 'temp1Celsius',
    expression: 'position.getString("temp1")',
    type: 'Number',
    unit: '°C',
    icon: 'Thermometer',
    validation: { min: -40, max: 120 },
    intervals: [],
  },
  {
    name: 'Battery Voltage',
    attribute: 'batteryVoltage',
    expression: 'power',
    type: 'Number',
    unit: 'V',
    icon: 'Zap',
    validation: { min: 10, max: 30 },
    intervals: [
      { threshold: 12.5, color: 'green' },
      { threshold: 11.5, color: 'yellow' },
      { threshold: null, color: 'red' },
    ],
  },
  {
    name: 'RPM',
    attribute: 'rpm',
    expression: 'rpm',
    type: 'Number',
    unit: 'rpm',
    icon: 'Gauge',
    validation: null,
    intervals: [],
  },
];
